/**
 * Audio Player for Mumble Voice Packets
 * Decodes Opus frames and plays them using Web Audio API
 *
 * Based on v1 (working) with improved jitter buffer and metrics.
 */

import { Decoder } from 'libopus.js';

const SAMPLE_RATE = 48000;

export default class AudioPlayer {
  constructor(sessionId, audioContext, outputGain) {
    this.sessionId = sessionId;
    this.audioContext = audioContext;
    this.outputGain = outputGain;

    // Per-player gain node — acts as a kill switch for reset().
    this._playerGain = this.audioContext.createGain();
    this._playerGain.gain.value = 1.0;
    this._playerGain.connect(this.outputGain);

    // Initialize Opus decoder (48kHz, mono)
    this.decoder = new Decoder({
      rate: SAMPLE_RATE,
      channels: 1,
      unsafe: true
    });

    // Playback timing
    this.nextPlaybackTime = 0;
    this.isFirstFrame = true;
    this._lastFrameTime = 0; // wall clock of last frame arrival

    // Dynamic jitter buffer
    this._connectionQuality = 'good';
    this._jitterBufferMs = 80;
    this._frameCount = 0;
    this._lateFrames = 0;

    // Stats
    this._totalFrames = 0;
    this._underruns = 0;
    this._skips = 0;
    this._peakAhead = 0;

    // Periodic metrics
    this._metricsInterval = setInterval(() => this._reportMetrics(), 5000);

    console.log(`[AudioPlayer] Created for session ${sessionId}, jitter=${this._jitterBufferMs}ms`);
  }

  setConnectionQuality(quality) {
    if (quality === this._connectionQuality) return;
    this._connectionQuality = quality;

    const bufferMap = { good: 80, warning: 140, poor: 200 };
    this._jitterBufferMs = bufferMap[quality] || 80;
    console.log(`[AudioPlayer] Session ${this.sessionId}: Quality=${quality}, jitter=${this._jitterBufferMs}ms`);
  }

  decodeAndPlay(opusFrame) {
    try {
      const buffer = Buffer.from(opusFrame);
      const pcmData = this.decoder.decodeFloat32(buffer);
      if (!pcmData || pcmData.length === 0) return;

      const audioBuffer = this.audioContext.createBuffer(1, pcmData.length, SAMPLE_RATE);
      audioBuffer.getChannelData(0).set(pcmData);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this._playerGain);

      const currentTime = this.audioContext.currentTime;
      const jitterBufferSec = this._jitterBufferMs / 1000;
      const now = Date.now();

      this._totalFrames++;
      this._frameCount++;

      // Detect new transmission: first frame or >500ms gap since last frame
      if (this.isFirstFrame || (now - this._lastFrameTime > 500)) {
        this.nextPlaybackTime = currentTime + jitterBufferSec;
        this.isFirstFrame = false;
        this._frameCount = 0;
        this._lateFrames = 0;
      }
      this._lastFrameTime = now;

      // Late frame — scheduled time already passed
      if (this.nextPlaybackTime < currentTime) {
        this._lateFrames++;
        this._underruns++;

        // Grow buffer on repeated underruns (every 3rd underrun adds 20ms)
        if (this._underruns % 3 === 0) {
          this._jitterBufferMs = Math.min(this._jitterBufferMs + 20, 250);
        }

        // Re-sync with full jitter buffer (not just 20ms)
        this.nextPlaybackTime = currentTime + (this._jitterBufferMs / 1000);
      }

      // Way too far ahead — burst pushed scheduling too far out
      if (this.nextPlaybackTime > currentTime + 0.3) {
        this.nextPlaybackTime = currentTime + (this._jitterBufferMs / 1000);
        this._skips++;
      }

      // Track how far ahead we're scheduled
      const ahead = this.nextPlaybackTime - currentTime;
      if (ahead > this._peakAhead) this._peakAhead = ahead;

      // Play the buffer
      source.start(this.nextPlaybackTime);

      // Advance by actual frame duration
      this.nextPlaybackTime += audioBuffer.duration;

    } catch (err) {
      console.error(`[AudioPlayer] Session ${this.sessionId}: Decode error:`, err);
    }
  }

  _reportMetrics() {
    if (this._totalFrames === 0) return;
    const ahead = Math.max(0, this.nextPlaybackTime - this.audioContext.currentTime);
    console.log(`[AudioMetrics] session=${this.sessionId} frames=${this._totalFrames} underruns=${this._underruns} skips=${this._skips} late=${this._lateFrames} aheadMs=${Math.round(ahead * 1000)} peakAheadMs=${Math.round(this._peakAhead * 1000)} jitterMs=${this._jitterBufferMs} firstFrame=${this.isFirstFrame}`);
  }

  reset() {
    this._playerGain.disconnect();
    this._playerGain = this.audioContext.createGain();
    this._playerGain.gain.value = 1.0;
    this._playerGain.connect(this.outputGain);

    this.isFirstFrame = true;
    this.nextPlaybackTime = 0;
    this._frameCount = 0;
    this._lateFrames = 0;
    this._underruns = 0;
    this._skips = 0;
    this._peakAhead = 0;
    console.log(`[AudioPlayer] Session ${this.sessionId}: Reset (jitter ${this._jitterBufferMs}ms)`);
  }

  destroy() {
    try {
      this._reportMetrics();
      if (this._metricsInterval) {
        clearInterval(this._metricsInterval);
        this._metricsInterval = null;
      }
      if (this._playerGain) {
        this._playerGain.disconnect();
        this._playerGain = null;
      }
      if (this.decoder) {
        this.decoder.destroy();
        this.decoder = null;
      }
      console.log(`[AudioPlayer] Session ${this.sessionId}: Destroyed`);
    } catch (err) {
      console.error(`[AudioPlayer] Session ${this.sessionId}: Error during destroy:`, err);
    }
  }
}
